import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useB2BTranslation } from "../hooks/useB2BTranslation";
import { AlertCircle, ArrowRight } from "lucide-react";
import { useB2BAuth } from "../B2BAuthContext/B2BAuthContext";
import { checkProfileCompletion } from "../utils/mandatoryFields";
import { api } from "../../../../utils/apiConfig";
import "./B2BProfileCompletionAlert.css";

interface B2BProfileCompletionAlertProps {
  className?: string;
  style?: React.CSSProperties;
}

const B2BProfileCompletionAlert: React.FC<B2BProfileCompletionAlertProps> = ({
  className = "",
  style = {},
}) => {
  const { t } = useB2BTranslation();
  const { user } = useB2BAuth();
  const [isProfileComplete, setIsProfileComplete] = useState<boolean | null>(null);

  useEffect(() => {
    const checkProfileCompletionStatus = async () => {
      try {
        // First check if user has basic profile data
        if (!user) {
          setIsProfileComplete(false);
          return;
        }

        // Fetch the latest profile data from the API
        const response = await api.getProfile();
        const latestProfileData = response.data;

        // Check mandatory fields using the latest profile data
        const profileData = {
          name: latestProfileData.name,
          surname: latestProfileData.surname,
          phoneNumber: latestProfileData.phoneNumber,
          position: latestProfileData.position,
          linkedin: latestProfileData.linkedin,
          businesses: latestProfileData.businesses || [],
          interestedIn: latestProfileData.interestedIn || []
        };

        const { isComplete, missingFields } = checkProfileCompletion(profileData);
        console.log("=== PROFILE COMPLETION DEBUG ===");
        console.log("Latest profile data:", latestProfileData);
        console.log("Profile data for validation:", profileData);
        console.log("Is complete:", isComplete);
        console.log("Missing fields:", missingFields);
        setIsProfileComplete(isComplete);
      } catch (error) {
        console.error("Error checking profile completion:", error);
        setIsProfileComplete(false);
      }
    };

    checkProfileCompletionStatus();
  }, [user]);

  // Don't render if profile is completed or still checking
  if (isProfileComplete === true || isProfileComplete === null) {
    return null;
  }

  return (
    <div className={`member-completion-alert ${className}`} style={style}>
      <div className="alert-container active">
        <div className="alert-icon-wrapper">
          <AlertCircle size={20} />
        </div>
        <div className="alert-content">
          <h3 className="alert-title">{t("auth.completeMember")}</h3>
          <p className="alert-description">
            {t("auth.memberCompletionDescription")}
          </p>
        </div>
        <Link
          to="/gcb/profile/create"
          state={{
            editMode: true,
            userId: user?._id,
            email: user?.email,
            fullName: user?.name
              ? `${user.name} ${user.surname || ""}`.trim()
              : "",
            countryCode: user?.countryCode || "+1",
            phoneNumber: user?.phoneNumber || "",
          }}
          className="alert-action-button"
        >
          {t("auth.completeMember")}
          <ArrowRight size={16} />
        </Link>
      </div>
    </div>
  );
};

export default B2BProfileCompletionAlert;
